import { ArrowLeft, ArrowRight, CheckCircle2, ExternalLink, Printer, ShoppingCart } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { productCatalog } from '../../products/data/products';
import { useShop } from '../../../shared/store';
import SafeImage from '../../../shared/components/SafeImage';
import { buildFallbackPodListing, podBooksService, type PodBookListing } from '../../../shared/services';
import { paymentService } from '../../../shared/services/paymentService';
import { convertRwfToUsd, formatRwf, formatUsd } from '../../../shared/utils/currency';

const formatPodLabel = (value: string | null | undefined): string => {
  if (!value) {
    return 'Not specified';
  }
  return value
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const PodBookDetailsPage = () => {
  const { productId } = useParams<{ productId?: string }>();
  const navigate = useNavigate();
  const { addToCart, currentUser, products, themeMode } = useShop();
  const isDark = themeMode === 'dark';
  const [podBooks, setPodBooks] = useState<PodBookListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [usdToRwfRate, setUsdToRwfRate] = useState<number | null>(null);
  const checkoutEditionStorageKey = 'haapa.checkout.edition';
  const checkoutPodProjectStorageKey = 'haapa.checkout.podProjectId';

  const fallbackPodBooks = useMemo(() => {
    const catalogSource = products.length > 0 ? products : productCatalog;
    const livePodBooks = catalogSource.filter(
      (product) => (product.mediaType ?? 'book') === 'book' && Boolean(product.isPODAvailable),
    );
    const source = livePodBooks.length > 0 ? livePodBooks : productCatalog.filter((product) => Boolean(product.isPODAvailable));
    return source.map((product) => buildFallbackPodListing(product));
  }, [products]);

  useEffect(() => {
    let isMounted = true;

    const loadPodBooks = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const data = await podBooksService.getPodBooks();
        if (!isMounted) {
          return;
        }
        setPodBooks(data);
      } catch (error: unknown) {
        if (!isMounted) {
          return;
        }
        setLoadError(error instanceof Error ? error.message : 'Unable to load POD details right now.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadPodBooks();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rate = await paymentService.getCurrencyRate();
        if (!cancelled) setUsdToRwfRate(rate);
      } catch {
        // Optional - keep RWF-only display if config is missing.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const resolvedPodBooks = podBooks.length > 0 ? podBooks : fallbackPodBooks;

  const book = useMemo(
    () => resolvedPodBooks.find((item) => item.id === productId) ?? null,
    [productId, resolvedPodBooks],
  );

  const relatedPodBooks = useMemo(
    () => resolvedPodBooks.filter((item) => item.id !== productId).slice(0, 4),
    [productId, resolvedPodBooks],
  );

  const detailHighlights = useMemo(() => {
    if (!book) {
      return [];
    }

    return [
      book.pageCount ? `${book.pageCount} pages prepared for print production` : 'Premium print-ready interior presentation',
      book.size ? `${book.size} trim configured for shelf-ready printing` : 'POD edition prepared for physical launch',
      book.coverType || book.pageColorType
        ? `${formatPodLabel(book.coverType)} cover with ${formatPodLabel(book.pageColorType)} pages`
        : 'Shelf-friendly trim and production flow',
    ];
  }, [book]);

  const handlePodPurchase = async (destination: 'cart' | 'checkout') => {
    if (!book) {
      return;
    }

    if (!currentUser) {
      void navigate('/auth');
      return;
    }

    try {
      const podResult = await directPodPaymentToFlutterwave(book.product, book.id);
      
      if (podResult.redirectUrl) {
        window.open(podResult.redirectUrl, '_blank');
        return;
      }

      // Fallback to cart flow
      window.sessionStorage.setItem(checkoutEditionStorageKey, 'print');
      window.sessionStorage.setItem(checkoutPodProjectStorageKey, book.id);
      addToCart(
        {
          ...book.product,
          price: book.finalPrice ?? book.printPrice ?? book.product.price,
          printPrice: book.printPrice,
          isPODAvailable: true,
          podProjectId: book.id,
        },
        1,
        { edition: 'print' },
      );

      if (destination === 'checkout') {
        void navigate('/checkout', { state: { edition: 'print', podProjectId: book.id } });
        return;
      }

      void navigate('/cart');
    } catch (error) {
      console.error('POD payment init failed:', error);
      // Fallback to standard cart/checkout flow
      window.sessionStorage.setItem(checkoutEditionStorageKey, 'print');
      window.sessionStorage.setItem(checkoutPodProjectStorageKey, book.id);
      addToCart(
        {
          ...book.product,
          price: book.finalPrice ?? book.printPrice ?? book.product.price,
          printPrice: book.printPrice,
          isPODAvailable: true,
          podProjectId: book.id,
        },
        1,
        { edition: 'print' },
      );

      if (destination === 'checkout') {
        void navigate('/checkout', { state: { edition: 'print', podProjectId: book.id } });
        return;
      }

      void navigate('/cart');
    }
  };

  if (isLoading && !book) {
    return (
      <section className={`relative overflow-hidden ${isDark ? 'bg-[#080606]' : 'bg-[#fdf6ef]'}`}>
        <div className="mx-auto max-w-7xl px-4 pb-20 pt-28 text-center sm:px-6 lg:px-8">
          <article className={`rounded-[28px] border px-5 py-12 ${isDark ? 'border-white/10 bg-white/5 text-slate-300' : 'border-orange-100 bg-white text-slate-600'}`}>
            Loading POD book details...
          </article>
        </div>
      </section>
    );
  }

  if (!book) {
    return <Navigate to="/pod" replace />;
  }

  return (
    <section className={`relative overflow-hidden ${isDark ? 'bg-[#080606]' : 'bg-[#fdf6ef]'}`}>
      <div
        className={`absolute inset-0 ${
          isDark
            ? 'bg-[radial-gradient(circle_at_14%_18%,rgba(249,115,22,0.24),transparent_26%),radial-gradient(circle_at_84%_16%,rgba(56,189,248,0.16),transparent_24%),linear-gradient(90deg,rgba(8,6,6,0.98)_0%,rgba(20,10,7,0.96)_42%,rgba(10,16,24,0.98)_100%)]'
            : 'bg-[radial-gradient(circle_at_14%_18%,rgba(255,180,96,0.26),transparent_26%),radial-gradient(circle_at_84%_16%,rgba(110,220,255,0.2),transparent_24%),linear-gradient(90deg,rgba(255,237,214,0.98)_0%,rgba(255,247,238,0.96)_42%,rgba(234,248,255,0.98)_100%)]'
        }`}
      />
      <div
        className={`absolute inset-0 [background-image:radial-gradient(rgba(148,163,184,0.22)_1px,transparent_1px)] [background-size:18px_18px] ${
          isDark ? 'opacity-35' : 'opacity-55'
        }`}
      />
      <div className={`pointer-events-none absolute -left-16 top-16 h-56 w-56 rounded-full blur-3xl ${isDark ? 'bg-orange-500/18' : 'bg-orange-300/30'}`} />
      <div className={`pointer-events-none absolute right-0 top-10 h-72 w-72 rounded-full blur-3xl ${isDark ? 'bg-sky-400/18' : 'bg-sky-200/35'}`} />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-12 pt-10 sm:px-6 sm:pt-10 lg:px-8 lg:pt-12">
        <Link
          to="/pod"
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition ${
            isDark
              ? 'border-white/10 bg-white/5 text-orange-200 hover:bg-white/10'
              : 'border-orange-200 bg-white text-orange-700 hover:bg-orange-50'
          }`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to POD collection
        </Link>

        <section
          className={`relative mt-4 overflow-hidden rounded-[32px] border bg-transparent px-4 py-5 sm:px-8 sm:py-8 lg:px-10 lg:py-10 ${
            isDark
              ? 'border-white/10 shadow-[0_30px_90px_-60px_rgba(0,0,0,0.7)]'
              : 'border-orange-100/70 shadow-[0_30px_90px_-60px_rgba(249,115,22,0.28)]'
          }`}
        >
          <div
            className={`absolute inset-0 ${
              isDark
                ? 'bg-[radial-gradient(circle_at_18%_12%,rgba(249,115,22,0.18),transparent_24%),radial-gradient(circle_at_88%_20%,rgba(255,255,255,0.08),transparent_24%),linear-gradient(90deg,rgba(40,18,10,0.45)_0%,rgba(255,255,255,0.06)_48%,rgba(16,32,46,0.28)_100%)]'
                : 'bg-[radial-gradient(circle_at_18%_12%,rgba(255,194,120,0.18),transparent_24%),radial-gradient(circle_at_88%_20%,rgba(255,255,255,0.35),transparent_24%),linear-gradient(90deg,rgba(255,239,221,0.62)_0%,rgba(255,255,255,0.26)_48%,rgba(220,244,255,0.42)_100%)]'
            }`}
          />
          <div className={`pointer-events-none absolute -left-10 top-8 h-44 w-44 rounded-full blur-3xl ${isDark ? 'bg-orange-500/18' : 'bg-orange-400/20'}`} />
          <div className={`pointer-events-none absolute right-8 top-10 h-56 w-56 rounded-full blur-3xl ${isDark ? 'bg-sky-400/16' : 'bg-sky-200/30'}`} />

          <div className="relative grid gap-8 lg:grid-cols-[0.88fr_1.12fr] lg:items-center">
            <div className="relative mx-auto w-full max-w-[320px] sm:max-w-[340px]">
              <div className={`absolute -inset-8 rounded-full blur-3xl ${isDark ? 'bg-orange-500/20' : 'bg-orange-400/18'}`} />
              <div className="relative overflow-hidden rounded-[30px] border border-white/70 bg-[#120d0b] shadow-[0_32px_90px_-45px_rgba(15,23,42,0.34)]">
                <SafeImage src={book.imageUrl} alt={book.title} className="h-[360px] w-full object-cover sm:h-[460px]" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent px-5 pb-5 pt-20 text-white">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-100/80">{book.author}</p>
                  <h1 className="mt-2 line-clamp-3 text-3xl font-black leading-tight">{book.title}</h1>
                </div>
              </div>
            </div>

            <div
              className={`max-w-3xl rounded-[32px] border p-5 shadow-[0_26px_70px_-50px_rgba(15,23,42,0.22)] backdrop-blur-xl sm:p-7 ${
                isDark ? 'border-white/10 bg-[#120d0b]/78 shadow-[0_26px_70px_-50px_rgba(0,0,0,0.65)]' : 'border-white/70 bg-white/72'
              }`}
            >
              <div
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] shadow-sm backdrop-blur ${
                  isDark ? 'border-white/10 bg-white/5 text-slate-200' : 'border-orange-200 bg-white/88 text-slate-700'
                }`}
              >
                <Printer className="h-4 w-4 text-orange-500" />
                POD Book Detail
              </div>

              <p className={`mt-6 text-sm font-semibold uppercase tracking-[0.22em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Live print edition showcase
              </p>
              <h2 className={`mt-2 text-3xl font-black uppercase leading-[0.92] sm:text-5xl ${isDark ? 'text-white' : 'text-slate-950'}`}>
                Ready to print,
                <br />
                <span className="text-orange-500">ready to review.</span>
              </h2>
              <p className={`mt-5 max-w-2xl text-base font-medium leading-7 sm:text-lg sm:leading-8 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                {book.description}
              </p>

              {loadError ? <p className="mt-4 text-sm font-semibold text-orange-500">{loadError}</p> : null}

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <div
                  className={`rounded-[24px] border px-4 py-4 shadow-[0_20px_50px_-40px_rgba(249,115,22,0.45)] backdrop-blur ${
                    isDark ? 'border-white/10 bg-white/5' : 'border-orange-100 bg-white/78'
                  }`}
                >
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Print price</p>
                  {usdToRwfRate && usdToRwfRate > 0 ? (
                    <>
                      <p className={`mt-2 text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-950'}`}>
                        {formatUsd(convertRwfToUsd(book.printPrice, usdToRwfRate))}
                      </p>
                      <p className={`mt-2 text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Pay in RWF: {formatRwf(book.printPrice)}
                      </p>
                    </>
                  ) : (
                    <p className={`mt-2 text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-950'}`}>RWF {book.printPrice.toLocaleString()}</p>
                  )}
                </div>
                <div
                  className={`rounded-[24px] border px-4 py-4 shadow-[0_20px_50px_-40px_rgba(249,115,22,0.45)] backdrop-blur ${
                    isDark ? 'border-white/10 bg-white/5' : 'border-orange-100 bg-white/78'
                  }`}
                >
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Format</p>
                  <p className={`mt-2 text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-950'}`}>{book.size ?? 'POD'}</p>
                </div>
                <div
                  className={`rounded-[24px] border px-4 py-4 shadow-[0_20px_50px_-40px_rgba(249,115,22,0.45)] backdrop-blur ${
                    isDark ? 'border-white/10 bg-white/5' : 'border-orange-100 bg-white/78'
                  }`}
                >
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Status</p>
                  <p className={`mt-2 text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-950'}`}>{formatPodLabel(book.podStatus)}</p>
                </div>
              </div>

              <div
                className={`mt-8 rounded-[28px] border p-5 shadow-[0_20px_50px_-36px_rgba(249,115,22,0.4)] ${
                  isDark ? 'border-white/10 bg-white/5' : 'border-orange-100 bg-white/80'
                }`}
              >
                <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-2xl">
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Where to buy it
                    </p>
                    <h3 className={`mt-2 text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-950'}`}>
                      Buy this POD book on Haapa Store
                    </h3>
                    <p className={`mt-2 text-sm leading-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Add the print edition to your cart, then continue to checkout to complete your POD order.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                <button
                  type="button"
                  onClick={() => handlePodPurchase('cart')}
                  className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all duration-150 sm:min-w-[140px] ${
                    isDark
                      ? 'border-white/12 bg-white/5 text-white hover:bg-white/10'
                      : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  <ShoppingCart className="h-4 w-4" />
                  Add to cart
                </button>
                <button
                  type="button"
                  onClick={() => handlePodPurchase('checkout')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_14px_32px_-18px_rgba(249,115,22,0.75)] transition-all duration-150 hover:-translate-y-0.5 hover:bg-orange-450 sm:min-w-[140px]"
                >
                  Buy now
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              {book.coverPreviewUrl ? (
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <a
                    href={book.coverPreviewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl border px-6 py-3.5 text-sm font-semibold transition sm:w-auto ${
                      isDark
                        ? 'border-white/10 bg-white/5 text-slate-200 hover:bg-white/10'
                        : 'border-slate-200 bg-white/85 text-slate-700 hover:bg-white'
                    }`}
                  >
                    Open cover preview
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              ) : null}

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {detailHighlights.map((item) => (
                  <div
                    key={item}
                    className={`rounded-[22px] border px-4 py-4 shadow-[0_16px_40px_-34px_rgba(249,115,22,0.34)] backdrop-blur ${
                      isDark ? 'border-white/10 bg-white/5' : 'border-orange-100 bg-white/72'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" />
                      <p className={`text-sm font-medium leading-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {relatedPodBooks.length > 0 ? (
          <section className="mt-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-500">More POD Books</p>
                <h2 className={`mt-2 text-4xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Explore similar printable editions</h2>
              </div>
            </div>

            <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {relatedPodBooks.map((relatedBook) => (
                <article
                  key={relatedBook.id}
                  className={`group overflow-hidden rounded-[24px] border transition hover:-translate-y-1 ${
                    isDark
                      ? 'border-white/10 bg-[#120d0b] shadow-[0_22px_70px_-52px_rgba(0,0,0,0.78)] hover:border-orange-400/40 hover:shadow-[0_30px_85px_-52px_rgba(249,115,22,0.32)]'
                      : 'border-orange-200 bg-white shadow-[0_22px_70px_-52px_rgba(249,115,22,0.48)] hover:shadow-[0_30px_85px_-52px_rgba(249,115,22,0.66)]'
                  }`}
                >
                  <div className="relative overflow-hidden">
                    <SafeImage
                      src={relatedBook.imageUrl}
                      alt={relatedBook.title}
                      className="h-56 w-full object-cover transition duration-500 group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/42 to-transparent px-4 pb-3 pt-16 text-white">
                      <h3 className="line-clamp-2 text-lg font-semibold leading-tight">{relatedBook.title}</h3>
                    </div>
                  </div>

                  <div className="space-y-3 p-4">
                    <p className={`line-clamp-2 text-sm leading-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{relatedBook.description}</p>
                    <div className={`flex flex-wrap items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {relatedBook.size ? <span>{relatedBook.size}</span> : null}
                      {relatedBook.pageCount ? <span>{relatedBook.pageCount} pages</span> : null}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Print price</p>
                        {usdToRwfRate && usdToRwfRate > 0 ? (
                          <>
                            <p className={`mt-1 text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {formatUsd(convertRwfToUsd(relatedBook.printPrice, usdToRwfRate))}
                            </p>
                            <p className={`mt-1 text-[11px] font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              Pay in RWF: {formatRwf(relatedBook.printPrice)}
                            </p>
                          </>
                        ) : (
                          <p className={`mt-1 text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>RWF {relatedBook.printPrice.toLocaleString()}</p>
                        )}
                      </div>
                      <Link
                        to={`/pod/${relatedBook.id}`}
                        className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-400"
                      >
                        View
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </section>
  );
};

export default PodBookDetailsPage;
